export interface FilterOption {
    text: string;
    value: string;
    isDefault?: boolean;
}

export interface FilterType {
    field: string;
    options: FilterOption[];
    text: string;
    title?: string;
    type?: "text" | "date" | "date_range_utc" | "date_range" | "price_range" | "staff" | "category_group";
    dataTestId?: string;
}

/**
 * Retrieves the text representation of a given value based on the provided filter types and field.
 * @param {FilterType[]} filterTypes - An array of FilterType objects containing field and options information.
 * @param {string} field - The specific field to search for in the filterTypes array.
 * @param {string} value - The value to match against the options of the found field.
 * @returns {string} The corresponding text for the matched value, or an empty string if no match is found.
 */
export const valueToText = (filterTypes: FilterType[], field: string, value: string): string => {
    let options: FilterOption[] = [];
    let text = "";

    filterTypes.forEach((filterType) => {
        if (filterType.field === field) {
            options = filterType.options;
        }
    });

    options.forEach((option) => {
        if (option.value === value) text = option.text;
    });

    return text;
};

export const MaterialFilterType: FilterType[] = [
    {
        field: "storage",
        options: [{ text: "Kho 1", value: "Kho 1" }, { text: "Kho 2", value: "Kho 2" }],
        text: "Tất cả kho",
        title: "Kho",
    },
    {
        field: "is_below_threshold",
        options: [
            {
                text: "Dưới mức",
                value: "true",
            },
            {
                text: "Trên mức",
                value: "false",
            },
        ],
        text: "Tất cả trạng thái",
        title: "Cảnh báo tồn kho",
    },
    {
        field: "expired_date",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày đến hạn",
        type: "date_range_utc",
    },
];

export const ProductOrderFilterType: FilterType[] = [
    {
        field: "order_type",
        options: [
            {
                text: "Đơn bán hàng",
                value: "SALE",
            },
            {
                text: "Đơn dự phòng",
                value: "RESERVE",
            },
        ],
        text: "Tất cả loại đơn",
        title: "Loại đơn",
        dataTestId: "order-type-filter",
    },
    {
        field: "status",
        options: [
            {
                text: "Mở",
                value: "OPEN",
            },
            {
                text: "Hậu cần xử lý",
                value: "LOGISTIC_REVIEWED",
            },
            {
                text: "Lãnh vật tư & Sản xuất",
                value: "MATERIAL_REQUESTED_AND_MANUFACTURING",
            },
            {
                text: "Nhập kho & Xuất kho",
                value: "IMPORTING_AND_EXPORTING",
            },
            {
                text: "Hoàn thành",
                value: "COMPLETED",
            },
            {
                text: "Đã Huỷ",
                value: "CANCELLED",
            },
        ],
        text: "Tất cả trạng thái",
        title: "Trạng thái",
        dataTestId: "status-filter",
    },
    {
        field: "progress_status",
        options: [
            {
                text: "Chưa có đề nghị nhập kho",
                value: "NO_IMPORT_REQUEST",
            },
            {
                text: "Chưa nhập kho",
                value: "NOT_IMPORTED",
            },
            {
                text: "Đã nhập một phần",
                value: "PARTIALLY_IMPORTED",
            },
            {
                text: "Đã nhập đủ",
                value: "ALL_IMPORTED",
            },
            {
                text: "Chưa có chuyến xe",
                value: "NO_DELIVERY_TRIP",
            },
            {
                text: "Chưa xuất xe",
                value: "NOT_EXPORTED",
            },
            {
                text: "Đã xuất một phần",
                value: "PARTIALLY_EXPORTED",
            },
            {
                text: "Đã xuất tất cả chuyến",
                value: "ALL_EXPORTED",
            },
        ],
        text: "Tất cả trạng thái chi tiết",
        title: "Trạng thái chi tiết",
        dataTestId: "progress-status-filter",
    },
    {
        field: "due_date",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày đến hạn",
        type: "date_range",
    },
    {
        field: "order_price",
        options: [],
        text: "Giá trong khoảng",
        title: "Giá",
        type: "price_range",
    },
    {
        field: "category",
        options: [
            {
                text: "Phuy",
                value: "Phuy",
            },
            {
                text: "Thùng",
                value: "Thùng",
            },
            {
                text: "Cơ Khí Ô Tô",
                value: "Cơ Khí Ô Tô",
            },
        ],
        text: "Tất cả loại sản phẩm",
        title: "Loại sản phẩm",
    },
];

export const ProductFilterType: FilterType[] = [
    {
        field: "storage",
        options: [
            {
                text: "Kho 1",
                value: "Kho 1",
            },
            {
                text: "Kho 2",
                value: "Kho 2",
            },
        ],
        text: "Tất cả kho",
        title: "Kho",
    },
    {
        field: "category_group",
        options: [],
        text: "Tất cả nhóm sản phẩm",
        title: "Nhóm sản phẩm",
        type: "category_group",
    },
    {
        field: "category",
        options: [
            {
                text: "Phuy",
                value: "Phuy",
            },
            {
                text: "Thùng",
                value: "Thùng",
            },
            {
                text: "Cơ Khí Ô Tô",
                value: "Cơ Khí Ô Tô",
            },
        ],
        text: "Tất cả danh mục",
        title: "Danh mục",
    },
    {
        field: "is_active",
        options: [
            {
                text: "Hoạt động",
                value: "true",
            },
            {
                text: "Vô hiệu hoá",
                value: "false",
            },
        ],
        text: "Tất cả trạng thái",
        title: "Trạng thái",
    },
];

export const AddOnsFilterType: FilterType[] = [
    {
        field: "category",
        options: [
            {
                text: "Phuy",
                value: "Phuy",
            },
            {
                text: "Thùng",
                value: "Thùng",
            },
            {
                text: "Cơ Khí Ô Tô",
                value: "Cơ Khí Ô Tô",
            },
        ],
        text: "Tất cả danh mục",
        title: "Danh mục",
    },
];

export const GroupedDeliveryTripsFilterType: FilterType[] = [
    {
        field: "status",
        options: [
            {
                text: "Đang vận chuyển",
                value: "Processing",
            },
            {
                text: "Đã vận chuyển",
                value: "Done",
            },
            {
                text: "Chờ xử lý",
                value: "Pending",
                isDefault: true,
            },

        ],
        text: "Tất cả trạng thái",
        title: "Trạng thái",
    },
    {
        field: "scheduled_at",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày giao hàng",
        type: "date_range",
    },
];


export const ExtraFeesFilterType: FilterType[] = [
    {
        field: "category",
        options: [
            {
                text: "Phuy",
                value: "Phuy",
            },
            {
                text: "Thùng",
                value: "Thùng",
            },
            {
                text: "Cơ Khí Ô Tô",
                value: "Cơ Khí Ô Tô",
            },
        ],
        text: "Tất cả danh mục",
        title: "Danh mục",
    },
];

export const CustomerFilterType: FilterType[] = [
    {
        field: "tier",
        options: [
            {
                text: "Cấp độ 1",
                value: "tier 1",
            },
            {
                text: "Cấp độ 2",
                value: "tier 2",
            },
            {
                text: "Cấp độ 3",
                value: "tier 3",
            },
        ],
        text: "Tất cả cấp độ",
        title: "Cấp độ",
    },
    {
        field: "category",
        options: [
            {
                text: "Phuy",
                value: "Phuy",
            },
            {
                text: "Thùng",
                value: "Thùng",
            },
            {
                text: "Cơ Khí Ô Tô",
                value: "Cơ Khí Ô Tô",
            },
        ],
        text: "Tất cả danh mục",
        title: "Danh mục",
    },
];

export const MaterialOrderFilterType: FilterType[] = [
    {
        field: "is_urgent",
        options: [
            {
                text: "Gấp",
                value: "true",
            },
            {
                text: "Không gấp",
                value: "false",
            },
        ],
        text: "Tất cả độ ưu tiên",
        title: "Ưu tiên",
    },
    {
        field: "status",
        options: [
            {
                text: "Mở",
                value: "Open",
            },
            {
                text: "Đang Nhập Kho",
                value: "Importing",
            },
            {
                text: "Hoàn Thành",
                value: "Restock",
            },
            {
                text: "Đã Hủy",
                value: "Cancelled",
            },
        ],
        text: "Tất cả độ trạng thái",
        title: "Trạng thái",
    },
    // {
    //     field: "logistic_staff",
    //     type: "staff",
    //     options: [],
    //     text: "Tất cả nhân viên hậu cần",
    //     title: "Nhân viên hậu cần",
    //     dataTestId: "logistic-staff-filter",
    // },
    {
        field: "due_date",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày đến hạn",
        type: "date_range",
    },
    {
        field: "price",
        options: [],
        text: "Giá trong khoảng",
        title: "Giá",
        type: "price_range",
    },
];

export const MaterialRequestFilterType: FilterType[] = [
    {
        field: "is_urgent",
        options: [
            {
                text: "Gấp",
                value: "true",
            },
            {
                text: "Không gấp",
                value: "false",
            },
        ],
        text: "Tất cả độ ưu tiên",
        title: "Ưu tiên",
    },
    {
        field: "status",
        options: [
            {
                text: "Mở",
                value: "Open",
            },
            {
                text: "Chờ Duyệt",
                value: "Pending",
            },
            {
                text: "Đã Duyệt",
                value: "Approved",
            },
            {
                text: "Đang Xuất Kho",
                value: "Exporting",
            },
            {
                text: "Hoàn Thành",
                value: "Completed",
            },
            {
                text: "Đã Hủy",
                value: "Cancelled",
            },
            {
                text: "Từ Chối",
                value: "Declined",
            },
        ],
        text: "Tất cả độ trạng thái",
        title: "Trạng thái",
    },
    {
        field: "due_date",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày đến hạn",
        type: "date_range",
    },
];

export const OutsourceOrderFilterType: FilterType[] = [
    {
        field: "is_urgent",
        options: [
            {
                text: "Gấp",
                value: "true",
            },
            {
                text: "Không gấp",
                value: "false",
            },
        ],
        text: "Tất cả độ ưu tiên",
        title: "Ưu tiên",
    },
    {
        field: "status",
        options: [
            {
                text: "Mở",
                value: "Open",
            },
            {
                text: "Đang Xuất Kho",
                value: "Exporting",
            },
            {
                text: "Đã Nhập Một Phần",
                value: "PartiallyReturned",
            },
            {
                text: "Hoàn Thành",
                value: "Completed",
            },
            {
                text: "Đã Hủy",
                value: "Cancelled",
            },
        ],
        text: "Tất cả độ trạng thái",
        title: "Trạng thái",
    },
    // {
    //     field: "logistic_staff",
    //     type: "staff",
    //     options: [],
    //     text: "Tất cả nhân viên hậu cần",
    //     title: "Nhân viên hậu cần",
    //     dataTestId: "logistic-staff-filter",
    // },
    {
        field: "due_date",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày đến hạn",
        type: "date_range",
    },
    {
        field: "price",
        options: [],
        text: "Giá trong khoảng",
        title: "Giá",
        type: "price_range",
    },
];

export const EmployeeFilterType: FilterType[] = [
    {
        field: "role",
        options: [
            {
                text: "Manager",
                value: "MANAGER",
            },
            {
                text: "Nhân viên Sale",
                value: "SALE_STAFF",
            },
            {
                text: "Nhân viên hậu cần",
                value: "LOGISTIC_STAFF",
            },
            {
                text: "Thủ kho 1",
                value: "FIRST_STORAGE_KEEPER",
            },
            {
                text: "Thủ kho 2",
                value: "SECOND_STORAGE_KEEPER",
            },
            {
                text: "Người vận chuyển",
                value: "DELIVERER",
            },
        ],
        text: "Tất cả vai trò",
        title: "Vai trò",
    },
];

export const MaterialsExportFilterType: FilterType[] = [
    // {
    //     field: "storage",
    //     options: [
    //         {
    //             text: "Kho 1",
    //             value: "Kho 1",
    //         },
    //         {
    //             text: "Kho 2",
    //             value: "Kho 2",
    //         }
    //     ],
    //     text: "Tất cả kho",
    //     title: "Kho",
    // },
    {
        field: "created_at",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày xuất kho",
        type: "date_range_utc",
    },
];

export const MaterialsImportFilterType: FilterType[] = [
    {
        field: "created_at",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày nhập kho",
        type: "date_range_utc",
    },
];

export const ProductsExportFilterType: FilterType[] = [
    // {
    //     field: "storage",
    //     options: [
    //         {
    //             text: "Kho 1",
    //             value: "Kho 1",
    //         },
    //         {
    //             text: "Kho 2",
    //             value: "Kho 2",
    //         }
    //     ],
    //     text: "Tất cả kho",
    //     title: "Kho",
    // },
    {
        field: "export_date",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày xuất kho",
        type: "date_range_utc",
    },
];

export const ProductTransactionFilterType: FilterType[] = [
    {
        field: "transaction_date",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày tạo",
        type: "date_range_utc",
    },
    {
        field: "transaction_type",
        options: [
            {
                text: "Nhập kho",
                value: "IN",
            },
            {
                text: "Xuất kho",
                value: "OUT",
            },
        ],
        text: "Tất cả loại giao dịch",
        title: "Loại giao dịch",
    },
];

export const ProductsImportFilterType: FilterType[] = [
    {
        field: "import_date",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày nhập kho",
        type: "date_range_utc",
    },
];


export const ProductQuantitySummaryFilterType: FilterType[] = [
    {
        field: "storage",
        options: [
            {
                text: "Kho 1",
                value: "Kho 1",
            },
            {
                text: "Kho 2",
                value: "Kho 2",
            },
        ],
        text: "Tất cả kho",
        title: "Kho",
    },
    {
        field: "category",
        options: [
            {
                text: "Phuy",
                value: "Phuy",
            },
            {
                text: "Thùng",
                value: "Thùng",
            },
            {
                text: "Cơ Khí Ô Tô",
                value: "Cơ Khí Ô Tô",
            },
        ],
        text: "Tất cả danh mục",
        title: "Danh mục",
    },
    {
        field: "category_group",
        options: [],
        text: "Tất cả nhóm sản phẩm",
        title: "Nhóm sản phẩm",
        type: "category_group",
    },
];

export const FileFilterType: FilterType[] = [
    {
        field: "created_at",
        options: [],
        text: "Từ ngày - Đến ngày",
        title: "Ngày tải lên",
        type: "date_range_utc",
    },
];
